import axios from "axios";

import React, {

  useState,
  useEffect,

} from "react";

import {

  useNavigate,

  useParams,

} from "react-router-dom";

import {

  useDispatch,
  useSelector,

} from "react-redux";

import {

  ArrowLeft,

  ArrowRight,

  Banknote,

  Check,

  CreditCard,

  Leaf,

  LockKeyhole,

  MessageSquareText,

  ShoppingBag,

  Utensils,

} from "lucide-react";

import {

  createOrder,

} from "../../actions/orderActions";

import {

  useCart,

} from "../../components/CartContext";

import {

  useRestaurantSession,

} from "../../components/RestaurantSessionProvider";

import {

  isSessionExpiredError,

  getSessionExpiredMessage,

} from "../../utils/sessionErrorHandler";

import { fetchPriceSummary } from "../../actions/priceActions";
import "./css/OrderScreen.css";

/* =========================================================

   ORDER SCREEN

\========================================================= */

function OrderScreen() {

  const navigate =

    useNavigate();

  const dispatch =

    useDispatch();

  const {

    restaurantId,

    tableId,

  } = useParams();

  /* =====================================================

     DINING SESSION

  \===================================================== */

  const {

    sessionToken,

  } = useRestaurantSession();

  /* =====================================================

     CART

  \===================================================== */

  const {

    cart,

    clearCart,

  } = useCart();

  /* =====================================================

     PRICE SUMMARY FROM CHECKOUT

  \===================================================== */

  const { loading: priceLoading, summary: priceSummary, error: priceError } =
    useSelector((state) => state.price || {});

  useEffect(() => {
    if (!cart?.length) return;
    dispatch(fetchPriceSummary(restaurantId, cart.map((item) => ({
      menu_item_id: item.id,
      quantity: item.quantity,
    }))));
  }, [dispatch, restaurantId, cart]);

  const priceReady = !priceLoading && !priceError && Boolean(priceSummary) &&
    priceSummary.total != null && Number.isFinite(Number(priceSummary.total));
  const subtotal = priceSummary?.subtotal ?? 0;
  const tax = priceSummary?.tax ?? 0;
  const serviceCharge = priceSummary?.service_charge ?? 0;
  const total = priceSummary?.total ?? 0;

  /* =====================================================

     STATE

  \===================================================== */

  const [

    paymentMethod,

    setPaymentMethod,

  ] = useState("");

  const [

    notes,

    setNotes,

  ] = useState("");

  const [

    loading,

    setLoading,

  ] = useState(false);

  const [

    error,

    setError,

  ] = useState("");

  const [paymentOptions, setPaymentOptions] = useState(null);
  const onlineAllowed = paymentOptions?.allow_online_payment === true;
  const cashAllowed = paymentOptions?.allow_cash_payment === true;

  useEffect(() => {
    let active = true;
    setPaymentOptions(null);
    setPaymentMethod("");
    if (!restaurantId) return () => { active = false; };
    axios.get(`/api/restaurants/${encodeURIComponent(restaurantId)}/payment-options/`)
      .then(({ data }) => { if (active) setPaymentOptions(data); })
      .catch(() => {
        if (active) {
          setPaymentOptions({ allow_online_payment: false, allow_cash_payment: false });
          setError("Unable to load available payment methods. Please refresh.");
        }
      });
    return () => { active = false; };
  }, [restaurantId]);

  useEffect(() => {
    if (paymentOptions === null) return;
    setPaymentMethod((current) => {
      if (current === "CASH" && cashAllowed) return current;
      if (current === "ONLINE" && onlineAllowed) return current;
      return cashAllowed ? "CASH" : onlineAllowed ? "ONLINE" : "";
    });
  }, [paymentOptions, cashAllowed, onlineAllowed]);

  /* =====================================================

     HOME PATH

  \===================================================== */

  const homePath =

    `/${restaurantId}/table/${tableId}`;

  /* =====================================================

     FORMAT PRICE

  \===================================================== */

  const formatPrice = (value) => {

    return Number(

      value || 0

    ).toFixed(2);

  };

  /* =====================================================

     BACK TO CHECKOUT

  \===================================================== */

  const handleBack = () => {

    navigate(

      `${homePath}/checkout`

    );

  };

  /* =====================================================

     SELECT PAYMENT

  \===================================================== */

  const selectPaymentMethod = (

    method

  ) => {

    if (method === "ONLINE" && !onlineAllowed) return;
    if (method === "CASH" && !cashAllowed) return;
    setPaymentMethod(method);

    if (error) {

      setError("");

    }

  };

  /* =====================================================

     PLACE ORDER

  \===================================================== */

  const handlePlaceOrder =

    async () => {

      /* -------------------------------------------------

         PAYMENT METHOD

      \------------------------------------------------- */

      if (!paymentOptions ||
          (paymentMethod === "ONLINE" && !onlineAllowed) ||
          (paymentMethod === "CASH" && !cashAllowed) ||
          !paymentMethod) {

        setError(

          "Please select a payment method."

        );

        return;

      }

      /* -------------------------------------------------

         CART

      \------------------------------------------------- */

      if (

        !cart ||

        cart.length === 0

      ) {

        setError(

          "Your cart is empty."

        );

        return;

      }

      /* -------------------------------------------------

         DINING SESSION

      \------------------------------------------------- */

      if (!priceReady) {
      setError("Final price is not ready. Please wait and try again.");
      return;
    }

    if (!sessionToken) {

        setError(

          "Dining session is not ready. Please try again."

        );

        return;

      }

      try {

        setLoading(true);

        setError("");

        /* =================================================

           COMMON ORDER PAYLOAD

        \================================================= */

        const orderData = {

          items:

            cart.map(

              (item) => ({

                menu_item_id:

                  item.id,

                quantity:

                  item.quantity,

              })

            ),

          /* ---------------------------------------------

             DINING SESSION

          \--------------------------------------------- */

          session_token:

            sessionToken,

          /* ---------------------------------------------

             PAYMENT

          \--------------------------------------------- */

          payment_method:

            paymentMethod,

          /* ---------------------------------------------

             TABLE

          \--------------------------------------------- */

          table_id:

            Number(tableId),

          /* ---------------------------------------------

             NOTES

          \--------------------------------------------- */

          notes:

            notes.trim(),

        };

        /* =================================================

           CREATE ORDER

        \================================================= */

        const response =

          await dispatch(

            createOrder(

              restaurantId,

              orderData

            )

          );

        console.log(

          "CREATE ORDER RESPONSE:",

          response

        );

        const orderId =

          response?.order_id ||

          response?.id;

      const confirmedTotal = Number(response?.total ?? priceSummary?.total);
      if (!Number.isFinite(confirmedTotal)) {
        throw new Error("A valid order total was not returned by the server.");
      }

        if (!orderId) {

          throw new Error(

            "Order was created but no order ID was returned."

          );

        }

        /* =================================================

           CASH PAYMENT

        \================================================= */

        if (

          paymentMethod === "CASH"

        ) {

          clearCart();

          navigate(

            `${homePath}/success`,

            {

              state: {

                orderId,

                paymentMethod:

                  "CASH",

                total:

                  confirmedTotal,

              },

            }

          );

          return;

        }

        /* =================================================

           ONLINE PAYMENT

        \================================================= */

        if (

          paymentMethod === "ONLINE"

        ) {

          navigate(

            `${homePath}/payment`,

            {

              state: {

                orderId,

                subtotal:

                  Number(subtotal),

                tax:

                  Number(tax),

                serviceCharge:

                  Number(

                    serviceCharge

                  ),

                total:

                  confirmedTotal,

              },

            }

          );

        }

      } catch (err) {

        console.error(

          "Create order error:",

          err

        );

        /* =================================================

           DINING SESSION EXPIRED

        \================================================= */

        if (

          isSessionExpiredError(err)

        ) {

          setError(

            "Your table session has ended. " +

            "Please scan the table QR code again " +

            "to start a new session."

          );

          return;

        }

        /* =================================================

           OTHER ERRORS

        \================================================= */

        setError(

          getSessionExpiredMessage(err)

        );

      } finally {

        setLoading(false);

      }

    };

  /* =====================================================

     RENDER

  \===================================================== */

  return (

    <main className="theme9-order-page">

      {/* =================================================

          HEADER

      \================================================= */}

      <section className="theme9-order-header">

        <div className="theme9-order-container">

          <button

            type="button"

            className="theme9-order-back"

            onClick={handleBack}

          >

            <ArrowLeft

              size={16}

            />

            Back to Checkout

          </button>

          <div className="theme9-order-title-row">

            <div>

              <span className="theme9-order-label">

                ONE LAST STEP

              </span>

              <h1>

                Place Your Order

              </h1>

              <p>

                Choose how you would

                like to pay and send

                your order to the

                kitchen.

              </p>

            </div>

            {/* TABLE */}

            <div className="theme9-order-table">

              <span>

                TABLE

              </span>

              <strong>

                {tableId}

              </strong>

            </div>

          </div>

        </div>

      </section>

      {/* =================================================

          CONTENT

      \================================================= */}

      <section className="theme9-order-content">

        <div className="theme9-order-container">

          <div className="theme9-order-layout">

            {/* =================================================

                LEFT SIDE

            \================================================= */}

            <div className="theme9-order-main">

              {/* =================================================

                  PAYMENT METHOD

              \================================================= */}

              <section className="theme9-order-section">

                <div className="theme9-order-section-title">

                  <span>

                    01

                  </span>

                  <div>

                    <small>

                      PAYMENT METHOD

                    </small>

                    <h2>

                      Choose payment

                    </h2>

                  </div>

                </div>

                {/* =============================================

                    PAYMENT OPTIONS

                \============================================== */}

                <div className="theme9-payment-options">

                  {/* ===========================================

                      CASH

                  \============================================ */}

                  <button

                    type="button"

                    className={

                      `theme9-payment-option ${

                        cashAllowed && paymentMethod === "CASH"

                          ? "selected"

                          : ""

                      }`

                    }

                    disabled={!cashAllowed}
                    aria-disabled={!cashAllowed}
                    style={!cashAllowed ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                    onClick={() =>

                      selectPaymentMethod(

                        "CASH"

                      )

                    }

                  >

                    <div className="theme9-payment-icon">

                      <Banknote

                        size={23}

                      />

                    </div>

                    <div className="theme9-payment-info">

                      <strong>

                        Cash

                      </strong>

                      <span>

                        Pay by cash after

                        your order is served.

                      </span>

                    </div>

                    <div className="theme9-payment-check">

                      {paymentMethod ===

                        "CASH" && (

                        <Check

                          size={14}

                        />

                      )}

                    </div>

                  </button>

                  {/* ===========================================

                      ONLINE

                  \============================================ */}

                  <button

                    type="button"

                    className={

                      `theme9-payment-option ${

                        onlineAllowed && paymentMethod === "ONLINE"

                          ? "selected"

                          : ""

                      }`

                    }

                    disabled={!onlineAllowed}
                    aria-disabled={!onlineAllowed}
                    style={!onlineAllowed ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                    onClick={() =>

                      selectPaymentMethod(

                        "ONLINE"

                      )

                    }

                  >

                    <div className="theme9-payment-icon">

                      <CreditCard

                        size={22}

                      />

                    </div>

                    <div className="theme9-payment-info">

                      <strong>

                        Online Payment

                      </strong>

                      <span>

                        Secure online payment

                        powered by Razorpay.

                      </span>

                    </div>

                    <div className="theme9-payment-check">

                      {paymentMethod ===

                        "ONLINE" && (

                        <Check

                          size={14}

                        />

                      )}

                    </div>

                  </button>

                </div>

              </section>

              {/* =================================================

                  SPECIAL INSTRUCTIONS

              \================================================= */}

              <section className="theme9-order-section">

                <div className="theme9-order-section-title">

                  <span>

                    02

                  </span>

                  <div>

                    <small>

                      OPTIONAL

                    </small>

                    <h2>

                      Special instructions

                    </h2>

                  </div>

                </div>

                <div className="theme9-notes-box">

                  <div className="theme9-notes-icon">

                    <MessageSquareText

                      size={18}

                    />

                  </div>

                  <textarea

                    value={notes}

                    onChange={(event) =>

                      setNotes(

                        event.target.value

                      )

                    }

                    placeholder={

                      "Any special request for the kitchen? " +

                      "For example: less spicy, no onions..."

                    }

                    rows={5}

                  />

                  <span className="theme9-notes-leaf">

                    <Leaf

                      size={15}

                    />

                  </span>

                </div>

              </section>

              {/* =================================================

                  TABLE

              \================================================= */}

              <div className="theme9-order-table-card">

                <div className="theme9-order-table-number">

                  <span>

                    TABLE

                  </span>

                  <strong>

                    {tableId}

                  </strong>

                </div>

                <div className="theme9-order-table-info">

                  <span>

                    YOUR TABLE

                  </span>

                  <h3>

                    Table {tableId}

                  </h3>

                  <p>

                    Your order will be

                    prepared and served

                    at this table.

                  </p>

                </div>

                <div className="theme9-order-table-status">

                  <Check

                    size={17}

                  />

                </div>

              </div>

              {/* =================================================

                  FRESH NOTE

              \================================================= */}

              <div className="theme9-order-fresh-note">

                <span>

                  <Leaf

                    size={15}

                  />

                </span>

                <p>

                  Your meal will be

                  freshly prepared after

                  your order is confirmed.

                </p>

              </div>

              {/* =================================================

                  ERROR

              \================================================= */}

              {error && (

                <div className="theme9-order-error">

                  <strong>

                    Unable to continue

                  </strong>

                  <p>

                    {error}

                  </p>

                </div>

              )}

            </div>

            {/* =================================================

                ORDER SUMMARY

            \================================================= */}

            <aside className="theme9-order-summary">

              {/* ICON */}

              <div className="theme9-order-summary-icon">

                <ShoppingBag

                  size={20}

                />

              </div>

              <span className="theme9-order-summary-label">

                03 · ORDER SUMMARY

              </span>

              <h2>

                Your Order

              </h2>

              <p className="theme9-order-summary-subtitle">

                Review your order before

                confirming.

              </p>

              {/* =================================================

                  CART ITEMS

              \================================================= */}

              <div className="theme9-order-summary-items">

                {cart.map(

                  (item) => (

                    <div

                      className="theme9-order-summary-item"

                      key={item.id}

                    >

                      <div>

                        <span>

                          <small>

                            {item.quantity}×

                          </small>

                          {item.name}

                        </span>

                      </div>

                      <strong>

                        ₹

                        {formatPrice(

                          Number(

                            item.price

                          ) *

                          Number(

                            item.quantity

                          )

                        )}

                      </strong>

                    </div>

                  )

                )}

              </div>

              <div className="theme9-order-divider" />

              {/* =================================================

                  SUBTOTAL

              \================================================= */}

              <div className="theme9-order-price-row">

                <span>

                  Subtotal

                </span>

                <strong>

                  ₹{formatPrice(

                    subtotal

                  )}

                </strong>

              </div>

              {/* =================================================

                  TAX

              \================================================= */}

              <div className="theme9-order-price-row">

                <span>

                  Tax

                </span>

                <strong>

                  ₹{formatPrice(

                    tax

                  )}

                </strong>

              </div>

              {/* =================================================

                  SERVICE CHARGE

              \================================================= */}

              <div className="theme9-order-price-row">

                <span>

                  Service Charge

                </span>

                <strong>

                  ₹{formatPrice(

                    serviceCharge

                  )}

                </strong>

              </div>

              <div className="theme9-order-divider" />

              {/* =================================================

                  TOTAL

              \================================================= */}

              <div className="theme9-order-total">

                <div>

                  <span>

                    TOTAL

                  </span>

                  <small>

                    Including applicable

                    charges

                  </small>

                </div>

                <strong>

                  ₹{formatPrice(

                    total

                  )}

                </strong>

              </div>

              {/* =================================================

                  PAYMENT STATUS

              \================================================= */}

              <div

                className={

                  `theme9-order-payment-status ${

                    paymentMethod

                      ? "selected"

                      : ""

                  }`

                }

              >

                {paymentMethod ? (

                  <>

                    <Check

                      size={14}

                    />

                    <span>

                      {paymentMethod ===

                      "CASH"

                        ? "Cash payment selected"

                        : "Online payment selected"}

                    </span>

                  </>

                ) : (

                  <>

                    <CreditCard

                      size={14}

                    />

                    <span>

                      Select a payment method

                    </span>

                  </>

                )}

              </div>

              {/* =================================================

                  PLACE ORDER

              \================================================= */}

              <button

                type="button"

                className="theme9-place-order-button"

                onClick={

                  handlePlaceOrder

                }

                disabled={

                  loading ||
                  !priceReady ||

                  !paymentOptions ||
                  (paymentMethod === "ONLINE" && !onlineAllowed) ||
                  (paymentMethod === "CASH" && !cashAllowed) ||
                  !paymentMethod

                }

              >

                {loading ? (

                  <>

                    <span className="theme9-order-spinner" />

                    Creating Order...

                  </>

                ) : (

                  <>

                    Place Order

                    <ArrowRight

                      size={18}

                    />

                  </>

                )}

              </button>

              {/* =================================================

                  BACK

              \================================================= */}

              <button

                type="button"

                className="theme9-order-edit-button"

                onClick={handleBack}

                disabled={loading}

              >

                <ArrowLeft

                  size={14}

                />

                Back to Checkout

              </button>

              {/* =================================================

                  SECURE NOTE

              \================================================= */}

              <div className="theme9-order-secure-note">

                <LockKeyhole

                  size={14}

                />

                <span>

                  Your order information

                  is securely processed.

                </span>

              </div>

            </aside>

          </div>

        </div>

      </section>

    </main>

  );

}

export default OrderScreen;